/**
 * Helpers for turning addresses that appear in UI text into ping targets.
 */

const IPV4_PATTERN = /(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})/;

function octetsAreValid(octets: string[]): boolean {
    return octets.every((octet) => {
        const value = Number(octet);
        return Number.isInteger(value) && value >= 0 && value <= 255;
    });
}

/**
 * Strips leading zeros from a dotted-quad address.
 *
 * The processor's `ipt` console output is zero-padded (e.g. `127.000.000.001`) and
 * IpTableEntry passes it straight through, so it has to be normalised before it can be
 * used as a ping target or compared against anything.
 *
 * @returns the normalised address, or null when the input is not a dotted quad
 */
export function normalizeIpv4(value: string | null | undefined): string | null {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return null;

    const parts = trimmed.split('.');
    if (parts.length !== 4) return null;
    if (!parts.every((part) => /^\d{1,3}$/.test(part))) return null;
    if (!octetsAreValid(parts)) return null;

    return parts.map((part) => String(Number(part))).join('.');
}

/**
 * Pulls the first IPv4 address out of free text.
 *
 * Diagnostic messages have no dedicated IP field — addresses are interpolated into the
 * message or details text (e.g. "Client connected: 10.0.0.5") — so they have to be
 * recovered by scanning. Version strings such as "v1.2.3.4" are skipped.
 *
 * @returns the normalised address, or null when the text contains none
 */
export function extractIpv4(text: string | null | undefined): string | null {
    const value = text ?? '';
    if (!value) return null;

    let searchFrom = 0;
    while (searchFrom < value.length) {
        const slice = value.slice(searchFrom);
        const match = IPV4_PATTERN.exec(slice);
        if (!match) return null;

        const start = searchFrom + match.index;
        const end = start + match[0].length;
        searchFrom = end;

        // Skip dotted numbers that are part of a longer run of digits/dots — version
        // strings like "v1.2.3.4" or "1.2.3.4.5" are not addresses.
        const before = start > 0 ? value[start - 1] : '';
        const after = end < value.length ? value[end] : '';
        if (before === '.' || before === 'v' || before === 'V' || /\d/.test(before)) continue;
        if (after === '.' || /\d/.test(after)) continue;

        if (!octetsAreValid(match.slice(1, 5))) continue;

        return normalizeIpv4(match[0]);
    }

    return null;
}
